import {
	createContext,
	useCallback,
	useContext,
	useRef,
	useState,
} from "react";
import type { ReactNode } from "react";
import { DestructiveDialog } from "@/components/destructive-dialog.component";
import OrgaAdminGruppeService from "@/lib/orgaAdmin/orgaAdminGruppe.service";

interface UserGroupRemovalGuardContextValue {
	confirmIfHasAssignedKorrekturSteps: (
		organizationId: string,
		gruppeId: string,
		userId: string,
		gruppeTitle?: string,
	) => Promise<boolean>;
	confirmIfAnySelectedHasAssignedKorrekturSteps: (
		organizationId: string,
		gruppeId: string,
		userIds: string[],
		gruppeTitle?: string,
	) => Promise<boolean>;
	isChecking: boolean;
}

const UserGroupRemovalGuardContext =
	createContext<UserGroupRemovalGuardContextValue | null>(null);

/**
 * Provider for the user group removal guard (used to confirm if a user has assigned korrektur steps before removing them from a group)
 * @param param0
 * @returns
 */
export const UserGroupRemovalGuardProvider: React.FC<{
	children: React.ReactNode;
}> = ({ children }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const [warning, setWarning] = useState<string | undefined>(undefined);
	const [details, setDetails] = useState<ReactNode | undefined>(undefined);
	const [isChecking, setIsChecking] = useState(false);
	const resolverRef = useRef<((value: boolean) => void) | null>(null);

	/**
	 * Opens the dialog to confirm if a user has assigned korrektur steps before removing them from a group
	 * @param t - The title of the dialog
	 * @param d - The description of the dialog
	 * @param w - The warning of the dialog
	 * @returns
	 */
	const openDialog = useCallback(
		(t: string, d: string, w?: string, det?: ReactNode): Promise<boolean> => {
			setTitle(t);
			setDescription(d);
			setWarning(w);
			setDetails(det);
			setIsOpen(true);
			return new Promise<boolean>((resolve) => {
				resolverRef.current = resolve;
			});
		},
		[],
	);

	/**
	 * Confirms if a user has assigned korrektur steps before removing them from a group
	 * @param organizationId - The organization ID
	 * @param gruppeId - The group ID
	 * @param userId - The user ID
	 * @param gruppeTitle - The title of the group
	 * @returns
	 */
	const confirmIfHasAssignedKorrekturSteps = useCallback(
		async (
			organizationId: string,
			gruppeId: string,
			userId: string,
			gruppeTitle?: string,
		): Promise<boolean> => {
			try {
				setIsChecking(true);
				const count =
					await OrgaAdminGruppeService.getInstance().getKorrekturStepsCountForUser(
						organizationId,
						gruppeId,
						userId,
					);
				setIsChecking(false);
				if (count > 0) {
					const gTitle = gruppeTitle ?? "dieser Gruppe";
					return await openDialog(
						"Benutzer aus Gruppe entfernen?",
						`Der Benutzer hat ${count} zugewiesene Korrekturschritte in "${gTitle}". Möchtest du ihn trotzdem aus dieser Gruppe entfernen?`,
						"Diese Aktion kann nicht rückgängig gemacht werden.",
					);
				}
				return true;
			} catch (err) {
				setIsChecking(false);
				console.error(err);
				return false;
			}
		},
		[openDialog],
	);

	/**
	 * Confirms if ANY of the selected users have assigned korrektur steps before removing them from a group
	 */
	const confirmIfAnySelectedHasAssignedKorrekturSteps = useCallback(
		async (
			organizationId: string,
			gruppeId: string,
			userIds: string[],
			gruppeTitle?: string,
		): Promise<boolean> => {
			if (userIds.length === 0) return true;
			try {
				setIsChecking(true);
				const result =
					await OrgaAdminGruppeService.getInstance().getKorrekturStepsCountForUsersInGruppe(
						organizationId,
						gruppeId,
						userIds,
					);
				setIsChecking(false);
				const countsByUser: Record<string, number> = {};
				for (const [uid, count] of Object.entries(result)) {
					countsByUser[uid] = (countsByUser[uid] ?? 0) + (count ?? 0);
				}
				const affectedUsers = Object.values(countsByUser).filter(
					(c) => c > 0,
				).length;
				const totalAssigned = Object.values(countsByUser).reduce(
					(sum, c) => sum + (c > 0 ? c : 0),
					0,
				);

				if (totalAssigned > 0) {
					const gTitle = gruppeTitle ?? "dieser Gruppe";
					const det = (
						<div>
							<div>
								Betroffene Personen: <strong>{affectedUsers}</strong> /{" "}
								{userIds.length}
							</div>
							<div>
								Zugewiesene Korrekturschritte gesamt:{" "}
								<strong>{totalAssigned}</strong>
							</div>
						</div>
					);
					return await openDialog(
						"Ausgewählte Personen aus Gruppe entfernen?",
						`In "${gTitle}" haben ${affectedUsers} ausgewählte Personen insgesamt ${totalAssigned} zugewiesene Korrekturschritte. Möchtest du sie trotzdem aus dieser Gruppe entfernen?`,
						"Diese Aktion kann nicht rückgängig gemacht werden.",
						det,
					);
				}
				return true;
			} catch (err) {
				setIsChecking(false);
				console.error(err);
				return false;
			}
		},
		[openDialog],
	);

	/**
	 * Handles the confirmation of the dialog
	 */
	const handleConfirm = useCallback(() => {
		setIsOpen(false);
		setDetails(undefined);
		resolverRef.current?.(true);
		resolverRef.current = null;
	}, []);

	/**
	 * Handles the cancellation of the dialog
	 */
	const handleCancel = useCallback(() => {
		setIsOpen(false);
		setDetails(undefined);
		resolverRef.current?.(false);
		resolverRef.current = null;
	}, []);

	return (
		<UserGroupRemovalGuardContext.Provider
			value={{
				confirmIfHasAssignedKorrekturSteps,
				confirmIfAnySelectedHasAssignedKorrekturSteps,
				isChecking,
			}}
		>
			{children}
			<DestructiveDialog
				isOpen={isOpen}
				title={title}
				description={description}
				warning={warning}
				details={details}
				confirmLabel="Entfernen"
				onConfirm={handleConfirm}
				onCancel={handleCancel}
			/>
		</UserGroupRemovalGuardContext.Provider>
	);
};

/**
 * Hook to use the user group removal guard
 * @returns
 */
export const useUserGroupRemovalGuard =
	(): UserGroupRemovalGuardContextValue => {
		const ctx = useContext(UserGroupRemovalGuardContext);
		if (!ctx)
			throw new Error("useUserGroupRemovalGuard must be used within provider");
		return ctx;
	};
